// Helpers for splitting a long CDI experience into several sub-periods (ticket #136).

const MIN_SPLIT_MONTHS = 24;

/** Parse an ISO-ish date (YYYY-MM, YYYY-MM-DD, RFC3339) into a Date at the 1st of the month. Returns null if unparseable. */
function parseMonth(value?: string | null): Date | null {
  if (!value) return null;
  const m = value.match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!year || !month) return null;
  return new Date(Date.UTC(year, month - 1, 1));
}

/** Format a Date as YYYY-MM. */
function toMonthString(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/** Whole months between start and end (or now if end is null). 0 if start is missing/invalid. */
export function experienceDurationMonths(start?: string | null, end?: string | null): number {
  const s = parseMonth(start);
  if (!s) return 0;
  const e = parseMonth(end) ?? new Date();
  const months = (e.getUTCFullYear() - s.getUTCFullYear()) * 12 + (e.getUTCMonth() - s.getUTCMonth());
  return Math.max(0, months);
}

export interface SplittableInput {
  contract_type?: string | null;
  start?: string | null;
  end?: string | null;
  is_proven?: boolean;
}

/** A CDI (permanent) experience of >= 24 months. On the profile path, must also be declarative. */
export function isSplittable(input: SplittableInput, opts: { requireDeclarative?: boolean } = {}): boolean {
  if (input.contract_type !== 'permanent') return false;
  if (experienceDurationMonths(input.start, input.end) < MIN_SPLIT_MONTHS) return false;
  if (opts.requireDeclarative && input.is_proven) return false;
  return true;
}

export interface SplitPeriod {
  title: string;
  start: string;      // YYYY-MM
  end: string | null; // YYYY-MM, null = ongoing (last period only)
  skill_ids: string[];
}

/** Seed 2 contiguous periods split at the midpoint. Fully editable afterwards. */
export function buildSeedPeriods(title: string, start: string, end: string | null): SplitPeriod[] {
  const s = parseMonth(start);
  const e = parseMonth(end) ?? new Date();
  if (!s) {
    return [
      { title, start: start ?? '', end: null, skill_ids: [] },
      { title, start: start ?? '', end: end ?? null, skill_ids: [] },
    ];
  }
  const totalMonths = Math.max(2, (e.getUTCFullYear() - s.getUTCFullYear()) * 12 + (e.getUTCMonth() - s.getUTCMonth()));
  const midOffset = Math.floor(totalMonths / 2);
  const mid = new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + midOffset, 1));
  return [
    { title, start: toMonthString(s), end: toMonthString(mid), skill_ids: [] },
    { title, start: toMonthString(mid), end: end ? toMonthString(e) : null, skill_ids: [] },
  ];
}

/**
 * Validate a list of split periods against the original range.
 * Returns an array of i18n error keys (empty = valid).
 */
export function validateSplitPeriods(periods: SplitPeriod[], originalStart: string, originalEnd: string | null): string[] {
  const errors: string[] = [];
  if (periods.length < 2) errors.push('experience_split.error_min_periods');

  const oStart = parseMonth(originalStart);
  const oEnd = parseMonth(originalEnd);

  let prevEnd: Date | null = null;
  for (let i = 0; i < periods.length; i++) {
    const p = periods[i];
    const isLast = i === periods.length - 1;
    const ps = parseMonth(p.start);
    const pe = parseMonth(p.end);
    if (!ps) { errors.push('experience_split.error_missing_start'); continue; }
    if (p.end === null && !isLast) { errors.push('experience_split.error_ongoing_not_last'); continue; }
    if (p.end !== null && !pe) { errors.push('experience_split.error_missing_end'); continue; }
    if (pe && ps >= pe) errors.push('experience_split.error_start_after_end');
    if (oStart && ps < oStart) errors.push('experience_split.error_before_original');
    if (oEnd && pe && pe > oEnd) errors.push('experience_split.error_after_original');
    if (prevEnd && ps < prevEnd) errors.push('experience_split.error_overlap');
    prevEnd = pe ?? prevEnd;
  }
  return [...new Set(errors)];
}
